/**
 * Talep kaynağı: ziyaretçinin siteye nereden geldiği (yönlendiren site, UTM, reklam tıklaması),
 * girdiği sayfa, formdan hemen önce baktığı sayfa ve — analitik izni varsa — gezdiği sayfalar
 * (sayfa açıkken geçen etkin süre ve kaydırma oranıyla), tıkladığı son çağrı butonu, cihaz türü
 * ve ilk geliş bilgisi. Yalnızca form gönderilirken talebe eklenir; yönetim paneline ve bildirim
 * e-postasına düşer, reklam/analitik amacıyla üçüncü taraflarla paylaşılmaz.
 *
 * Saklama: varsayılan olarak yalnızca bellekte tutulur (çerez ya da depolama yok; sayfa
 * yenilenince iz baştan başlar) ve formla yalnızca temel bilgiler gider. Analitik çerez izni
 * verildiyse iz oturum boyunca sessionStorage'da ("dj_iz"), ilk geliş bilgisi localStorage'da
 * ("dj_ilk_gelis", 90 gün kullanılır) saklanır; izin geri alınınca ikisi de silinir.
 * Bkz. Çerez Politikası ve KVKK Aydınlatma Metni.
 */
import { analitikIzniVar, IZIN_DEGISTI } from "./cerez_izni";

type TiklamaTuru = "google-ads" | "meta" | "tiktok" | "microsoft-ads" | "linkedin";

interface Gelis {
  /** Siteye girilen sayfanın yolu (sorgu dizgisi olmadan) */
  giris: string;
  /** Dış yönlendiren adres (yalnız köken + yol); "site-ici" ya da yok */
  ref?: string;
  utm?: Record<string, string>;
  /** Reklam tıklama kimliği türü (gclid → google-ads, fbclid → meta …) */
  tik?: TiklamaTuru;
  /** İz bir sayfa yenilemesinden, geri/ileri ya da site içi tam yüklemeden (ör. yeni sekme) sonra başladı */
  yeniden?: boolean;
  t: number;
}
interface Sayfa {
  yol: string;
  t: number;
  /** Sekme görünürken bu sayfada geçen birikmiş süre (ms) */
  sure: number;
  /** Sayfa şu an görünürse görünür olduğu an; değilse null */
  aktif: number | null;
  kaydirma: number;
}
interface Iz { gelis: Gelis; sayfalar: Sayfa[]; atlanan?: number; cta?: { metin: string; yol: string } }

export interface TalepKaynagi {
  form: string;
  sayfa: string;
  giris?: string;
  ref?: string;
  utm?: Record<string, string>;
  tik?: TiklamaTuru;
  yeniden?: boolean;
  /** Formdan hemen önce bakılan (form sayfasından farklı) sayfa */
  onceki?: string;
  // Aşağıdakiler yalnızca analitik izniyle gönderilir
  cta?: { metin: string; yol: string };
  sayfalar?: { yol: string; sn: number; kaydirma: number }[];
  atlanan?: number;
  ilk?: { giris: string; ref?: string; utm?: Record<string, string>; tik?: TiklamaTuru; tarih: string };
  cihaz?: "mobil" | "tablet" | "masaustu";
}

const IZ_ANAHTAR = "dj_iz";
const ILK_ANAHTAR = "dj_ilk_gelis";
const ILK_SURE_MS = 90 * 24 * 60 * 60 * 1000;
const AYRI_ZIYARET_MS = 30 * 60 * 1000;
const EN_FAZLA_SAYFA = 25;
const UTM_ALANLARI = ["utm_source", "utm_medium", "utm_campaign", "utm_term", "utm_content"];
const TIKLAMA_KIMLIKLERI: [string, TiklamaTuru][] = [
  ["gclid", "google-ads"], ["gbraid", "google-ads"], ["wbraid", "google-ads"],
  ["fbclid", "meta"], ["ttclid", "tiktok"], ["msclkid", "microsoft-ads"], ["li_fat_id", "linkedin"],
];
// Formu açan butonlar data-cta ile işaretlidir; diğerleri metin ve adresten tanınır
const CTA_METNI = /analiz|randevu|teklif|aray|görüşme|toplant[ıi]|iletişim|whatsapp|hesapla|başvur|soru sor|kirala|rapor/i;
const CTA_ADRESI = /^\/randevu|^\/iletisim|wa\.me|^tel:|^#.*form/;

/** Sunucu talebi kaydedemediğinde formlarda gösterilen mesaj (talep kaybolmasın, ziyaretçi tekrar denesin) */
export const TALEP_HATA_MESAJI =
  "Talebiniz gönderilemedi. Bilgilerinizi (özellikle e-posta adresini) kontrol edip tekrar deneyin ya da WhatsApp'tan yazın.";

let iz: Iz | null = null;
let ilk: Gelis | null = null;

/** Kontrol karakterlerini atar ve uzunluğu sınırlar */
const kirp = (s: string, n: number) => {
  const temiz = s.replace(/[\u0000-\u001f\u007f]/g, " ");
  return temiz.length > n ? temiz.slice(0, n) : temiz;
};
/** Yüzde kodlu ve çözülmüş yolları tek biçime getirir (wouter yolu çözülmüş verir) */
const yolNormal = (p: string) => {
  let y = p;
  try { y = decodeURI(p); } catch { /* geçersiz kodlama: olduğu gibi */ }
  return kirp(y, 200);
};
const ayniSite = (host: string) => host.replace(/^www\./, "") === location.hostname.replace(/^www\./, "");

function depo(tur: "session" | "local"): Storage | null {
  try { return tur === "session" ? window.sessionStorage : window.localStorage; } catch { return null; }
}
function yaz(tur: "session" | "local", anahtar: string, deger: unknown) {
  try { depo(tur)?.setItem(anahtar, JSON.stringify(deger)); } catch { /* kota/engel: bellekteki iz yeterli */ }
}
function oku<T>(tur: "session" | "local", anahtar: string): T | null {
  try { const v = depo(tur)?.getItem(anahtar); return v ? (JSON.parse(v) as T) : null; } catch { return null; }
}
function temizle() {
  try { depo("session")?.removeItem(IZ_ANAHTAR); depo("local")?.removeItem(ILK_ANAHTAR); } catch { /* yok say */ }
  ilk = null;
}

function gezinmeTuru(): string {
  try { return (performance.getEntriesByType("navigation")[0] as PerformanceNavigationTiming | undefined)?.type ?? "navigate"; } catch { return "navigate"; }
}

function gelisOlustur(): Gelis {
  const p = new URLSearchParams(location.search);
  const utm: Record<string, string> = {};
  for (const a of UTM_ALANLARI) {
    const v = p.get(a);
    if (v) utm[a.slice(4)] = kirp(v, 100);
  }
  let ref: string | undefined;
  if (document.referrer) {
    try {
      const u = new URL(document.referrer);
      ref = ayniSite(u.hostname) ? "site-ici" : kirp(u.origin + u.pathname, 200);
    } catch { /* geçersiz referrer */ }
  }
  const tik = TIKLAMA_KIMLIKLERI.find(([k]) => p.has(k))?.[1];
  const tur = gezinmeTuru();
  const yeniden = ref === "site-ici" || tur === "reload" || tur === "back_forward";
  return {
    giris: yolNormal(location.pathname),
    ...(ref ? { ref } : {}),
    ...(Object.keys(utm).length ? { utm } : {}),
    ...(tik ? { tik } : {}),
    ...(yeniden ? { yeniden } : {}),
    t: Date.now(),
  };
}

function kaydet() {
  if (!iz) return;
  // İzin başka bir sekmede geri alındıysa bu sekmedeki kayıt da silinir
  if (!analitikIzniVar()) { temizle(); return; }
  yaz("session", IZ_ANAHTAR, iz);
  if (!ilk) {
    ilk = iz.gelis;
    yaz("local", ILK_ANAHTAR, ilk);
  }
}

/** Görünür olduğu süreyi sayfanın birikmiş süresine ekler ve sayacı durdurur */
function sureyiDurdur(s: Sayfa | undefined, simdi = Date.now()) {
  if (s?.aktif) { s.sure += Math.max(0, simdi - s.aktif); s.aktif = null; }
}
const sonSayfa = () => iz?.sayfalar[iz.sayfalar.length - 1];

function kaydirmaOlc() {
  const son = sonSayfa();
  if (!son) return;
  const h = document.documentElement.scrollHeight - window.innerHeight;
  // Sayfa henüz yüklenirken (kısa yükleme ekranı) ölçülmez; yoksa her sayfa %100 görünür
  if (h < 200) return;
  const oran = Math.min(100, Math.round((window.scrollY / h) * 100));
  if (oran > son.kaydirma) son.kaydirma = oran;
}

let olcumBekliyor = false;
function kaydirmaDinle() {
  if (olcumBekliyor) return;
  olcumBekliyor = true;
  requestAnimationFrame(() => { olcumBekliyor = false; kaydirmaOlc(); });
}

function gorunurlukDegisti() {
  const son = sonSayfa();
  if (!son) return;
  if (document.visibilityState === "visible") { if (!son.aktif) son.aktif = Date.now(); }
  else { sureyiDurdur(son); kaydet(); }
}

function ctaYakala(e: MouseEvent) {
  const el = (e.target as Element | null)?.closest?.("a,button");
  if (!el || !iz) return;
  // Formun kendi gönder butonu çağrı butonu sayılmaz
  if (el instanceof HTMLButtonElement && el.type === "submit" && el.form) return;
  const metin = (el.textContent || "").replace(/\s+/g, " ").trim();
  const adres = el.getAttribute("href") || "";
  if (!el.hasAttribute("data-cta") && !CTA_METNI.test(metin) && !CTA_ADRESI.test(adres)) return;
  iz.cta = { metin: kirp(metin || adres, 80), yol: yolNormal(location.pathname) };
  kaydet();
}

/** Uygulama açılırken bir kez çağrılır (main.tsx) */
export function izBaslat() {
  if (iz || typeof window === "undefined") return;
  const yeni = gelisOlustur();
  if (analitikIzniVar()) {
    const onceki = oku<Iz>("session", IZ_ANAHTAR);
    // Aynı sekmede yenileme, geri/ileri ya da site içi tam yükleme: mevcut iz sürer.
    // Yeni bir dış kaynaktan (ör. reklam tıklaması) gelindiyse yeni iz başlar.
    const yeniKaynak = !yeni.yeniden && (!!yeni.utm || !!yeni.tik || (!!yeni.ref && yeni.ref !== "site-ici"));
    if (onceki?.gelis && Array.isArray(onceki.sayfalar) && !yeniKaynak) {
      iz = onceki;
      // Önceki yüklemeden kalan sayaçlar kapatılır (pagehide çalışmadıysa süre şişmesin)
      for (const s of iz.sayfalar) { s.aktif = null; s.sure = Number(s.sure) || 0; }
    } else {
      iz = { gelis: yeni, sayfalar: [] };
    }
    const kayitli = oku<Gelis>("local", ILK_ANAHTAR);
    if (kayitli && Date.now() - kayitli.t < ILK_SURE_MS) ilk = kayitli;
    else if (kayitli) { try { depo("local")?.removeItem(ILK_ANAHTAR); } catch { /* yok say */ } }
  } else {
    temizle();
    iz = { gelis: yeni, sayfalar: [] };
  }
  sayfaGir(location.pathname);
  window.addEventListener("scroll", kaydirmaDinle, { passive: true });
  document.addEventListener("click", ctaYakala, true);
  document.addEventListener("visibilitychange", gorunurlukDegisti);
  window.addEventListener("pagehide", () => { sureyiDurdur(sonSayfa()); kaydet(); });
  window.addEventListener("pageshow", e => { if (e.persisted) gorunurlukDegisti(); });
  window.addEventListener("storage", e => { if (e.key === ILK_ANAHTAR && e.newValue === null) ilk = null; });
  window.addEventListener(IZIN_DEGISTI, () => {
    if (analitikIzniVar()) kaydet();
    else temizle();
  });
}

/** Her rota değişiminde çağrılır (App.tsx) */
export function sayfaGir(yol: string) {
  if (!iz) return;
  const y = yolNormal(yol);
  // Not: burada kaydırma ölçülmez; rota değişince DOM yeni sayfayı gösterir, oran eski sayfaya yazılırdı
  const son = sonSayfa();
  if (son?.yol === y) {
    // Yenilemeden sonra aynı sayfa: süre sayacı yeniden başlar
    if (!son.aktif && document.visibilityState !== "hidden") son.aktif = Date.now();
    return;
  }
  const simdi = Date.now();
  sureyiDurdur(son, simdi);
  iz.sayfalar.push({ yol: y, t: simdi, sure: 0, aktif: document.visibilityState === "hidden" ? null : simdi, kaydirma: 0 });
  // Giriş sayfası korunur, en eski ara sayfa düşer (her sayfanın süresi kendinde tutulduğu için süreler kaymaz)
  if (iz.sayfalar.length > EN_FAZLA_SAYFA) {
    iz.sayfalar.splice(1, 1);
    iz.atlanan = (iz.atlanan || 0) + 1;
  }
  kaydet();
}

function cihaz(): TalepKaynagi["cihaz"] {
  try {
    const dokunmatik = window.matchMedia("(pointer: coarse)").matches;
    if (!dokunmatik) return "masaustu";
    return Math.min(window.screen.width, window.screen.height) >= 600 ? "tablet" : "mobil";
  } catch {
    return undefined;
  }
}

/**
 * Form gönderilirken talebe eklenecek kaynak bilgisi. Analitik izni yoksa yalnızca temel bilgiler
 * (kanal, giriş sayfası, form sayfası ve ondan hemen önceki sayfa) gönderilir.
 */
export function talepKaynagi(form: string): TalepKaynagi | undefined {
  // Kaynak bilgisi yardımcıdır: hesaplanırken bir tarayıcıda hata çıkarsa talep yine gönderilir (kaynaksız)
  try {
    return talepKaynagiOlustur(form);
  } catch {
    return undefined;
  }
}

function talepKaynagiOlustur(form: string): TalepKaynagi {
  if (!iz) izBaslat();
  kaydirmaOlc();
  const simdi = Date.now();
  const s = iz?.sayfalar ?? [];
  const g = iz?.gelis;
  const sayfa = yolNormal(location.pathname);
  const onceki = [...s].reverse().find(p => p.yol !== sayfa)?.yol;
  const temel: TalepKaynagi = {
    form: kirp(form, 100),
    sayfa,
    giris: g?.giris,
    ref: g?.ref,
    utm: g?.utm,
    tik: g?.tik,
    yeniden: g?.yeniden,
    onceki,
  };
  if (!analitikIzniVar()) return temel;
  const cta = iz?.cta;
  // Aynı buton sonraki bir forma yanlışlıkla yazılmasın
  if (iz) iz.cta = undefined;
  // Önceki ziyaret ayrı bir oturumsa ya da bu iz yeni sekme/yenileme yüzünden kaynaksız başladıysa eklenir
  const ilkZiyaret = ilk && g && ilk.t < g.t && (g.t - ilk.t > AYRI_ZIYARET_MS || g.yeniden) ? ilk : null;
  return {
    ...temel,
    cta,
    sayfalar: s.map(p => ({
      yol: p.yol,
      sn: Math.min(86400, Math.round((p.sure + (p.aktif ? simdi - p.aktif : 0)) / 1000)),
      kaydirma: p.kaydirma,
    })),
    atlanan: iz?.atlanan,
    ilk: ilkZiyaret
      ? { giris: ilkZiyaret.giris, ref: ilkZiyaret.ref, utm: ilkZiyaret.utm, tik: ilkZiyaret.tik, tarih: new Date(ilkZiyaret.t).toISOString() }
      : undefined,
    cihaz: cihaz(),
  };
}
